"use client"

import { AuthGuard } from '@/components/auth-guard'
import Link from "next/link"
import { useState, useEffect, Suspense, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { useAuth } from "@/lib/auth"
import { useToast } from "@/components/ui/toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  IconQrcode,
  IconPlus,
  IconDownload,
  IconCopy,
  IconTrash,
  IconEye,
  IconEdit,
  IconShare,
  IconUsers,
  IconTrendingUp,
  IconClock,
  IconCircleCheck,
  IconDots,
  IconLoader2,
  IconToggleLeft,
  IconToggleRight,
  IconUpload,
  IconX,
  IconPhoto,
  IconChevronDown,
  IconRocket,
  IconAlertCircle
} from "@tabler/icons-react"
import {
  getQRCampaigns,
  createQRCampaign,
  updateQRCampaign,
  deleteQRCampaign,
  publicGetGames,
  getAccountUsage
} from "@/lib/api-client"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { Slider } from "@/components/ui/slider"
import { EmailRichTextEditor } from "@/components/email-rich-text-editor"
import { cn } from "@/lib/utils"

interface Game {
  id: string
  game_name: string
  game_code: string
  icon: string
  category: string
}

interface UsageItem {
  current: number
  limit: number
  percentage?: number
  capacity?: number
}

interface AccountUsageData {
  period: { start: string; end: string }
  usage: Record<string, UsageItem>
}

interface QRCampaign {
  id: string
  name: string
  description: string
  qr_url: string
  total_scans?: number
  unique_scans?: number
  total_participants?: number
  current_uses?: number
  max_uses?: number
  is_one_time_use?: boolean
  game_id?: string
  game_name?: string
  game_icon?: string
  status: "active" | "paused" | "expired" | "draft"
  created_at: string
  created_by?: string
  start_date?: string
  end_date?: string
  campaign_type?: string
  budget?: number
  total_spent?: number
  qr_code_image?: string
  location?: string
  enable_customization?: boolean
  logo_image?: string
  background_wallpaper?: string
  button_color_theme?: string
  button_background_color?: string
  button_gradient_mode?: boolean
  button_gradient_end_color?: string
  card_glassmorphic?: boolean
  primary_text_color?: string
  secondary_text_color?: string
  button_text_color?: string
  start_playing_button_text?: string
  continue_to_game_button_text?: string
  player_title?: string
  player_short_description?: string
  player_detailed_instructions?: string
  email_sender_name?: string
  thank_you_email_subject?: string
  thank_you_email_body?: string
  thank_you_email_mode?: "editor" | "html"
}

// Image Upload Component
interface ImageUploadProps {
  label: string
  url: string
  onUrlChange: (url: string) => void
  file: File | null
  onFileChange: (file: File | null) => void
  preview: string
  onPreviewChange: (preview: string) => void
  helpText?: string
  aspectRatio?: 'square' | 'portrait' | 'landscape'
  required?: boolean
  hasError?: boolean
}

function ImageUpload({ label, url, onUrlChange, file, onFileChange, preview, onPreviewChange, helpText, aspectRatio = 'square', required, hasError }: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) {
      onFileChange(null)
      onPreviewChange('')
      return
    }

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 50MB)
    if (selectedFile.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB.')
      return
    }

    onFileChange(selectedFile)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      onPreviewChange(reader.result as string)
    }
    reader.readAsDataURL(selectedFile)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      handleFileSelect(droppedFile)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleRemoveFile = () => {
    handleFileSelect(null)
    onUrlChange('') // Also clear the URL
  }

  const displayPreview = preview || url

  return (
    <div className="space-y-2">
      <Label>{label}{required ? <span className="text-destructive"> *</span> : null}</Label>
      <div className="space-y-2">
        {/* Drag & Drop Area */}
        <div
          className={cn(
            "relative border-2 border-dashed rounded-lg p-4 transition-colors",
            hasError && "border-destructive ring-2 ring-destructive/20",
            !hasError && (isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50")
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {displayPreview ? (
            // Image Preview
            <div className="flex items-start gap-2">
              <div className={`rounded overflow-hidden flex-shrink-0 ${
                aspectRatio === 'portrait'
                  ? 'w-32 aspect-[9/16]'
                  : aspectRatio === 'landscape'
                  ? 'w-full aspect-[16/9]'
                  : 'w-32 aspect-square'
              }`}>
                <img
                  src={displayPreview}
                  alt={label}
                  className="w-full h-full object-cover"
                  onError={() => {
                    onPreviewChange('')
                  }}
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="flex-shrink-0"
                onClick={handleRemoveFile}
              >
                <IconX className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            // Upload Prompt
            <div className="text-center py-6">
              <IconPhoto className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">
                Drag & drop an image here
              </p>
              <p className="text-xs text-muted-foreground mb-1">
                or click to browse
              </p>
              <p className="text-xs text-muted-foreground/70 mb-3">
                Max file size: 50MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <IconUpload className="h-4 w-4 mr-2" />
                Choose File
              </Button>
            </div>
          )}
        </div>

        {/* URL Input (Alternative) - hide raw base64 from customers */}
        <div className="flex items-center gap-2">
          {url.startsWith('data:') ? (
            <Input
              type="url"
              placeholder="Image uploaded — enter URL to replace"
              value=""
              onChange={(e) => onUrlChange(e.target.value)}
              className="flex-1"
            />
          ) : (
            <Input
              type="url"
              placeholder="Or enter image URL"
              value={url}
              onChange={(e) => onUrlChange(e.target.value)}
              className="flex-1"
            />
          )}
        </div>

        {helpText && (
          <p className="text-xs text-muted-foreground">{helpText}</p>
        )}
      </div>
    </div>
  )
}

function QRCodesContent() {
  const searchParams = useSearchParams()
  const { user, getMerchantId } = useAuth()
  const { addToast } = useToast()
  const [campaigns, setCampaigns] = useState<QRCampaign[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const createDialogScrollRef = useRef<HTMLDivElement>(null)
  const createDialogContentRef = useRef<HTMLDivElement>(null)
  const [createDialogShowScrollHint, setCreateDialogShowScrollHint] = useState(false)
  const [createDialogHasOverflow, setCreateDialogHasOverflow] = useState(false)
  const editDialogScrollRef = useRef<HTMLDivElement>(null)
  const editDialogContentRef = useRef<HTMLDivElement>(null)
  const [editDialogShowScrollHint, setEditDialogShowScrollHint] = useState(false)
  const [editDialogHasOverflow, setEditDialogHasOverflow] = useState(false)
  const [accountUsage, setAccountUsage] = useState<AccountUsageData | null>(null)
  const [accountUsageLoading, setAccountUsageLoading] = useState(false)
  const [isGeneratingQR, setIsGeneratingQR] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<QRCampaign | null>(null)
  const [isQRModalOpen, setIsQRModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    game_id: "",
    campaign_type: "",
    is_one_time_use: true,
    max_uses: 1,
    location: "",
    expires_at: "",
    enable_customization: false,
    logo_image: "",
    background_wallpaper: "",
    button_color_theme: "#7461FD",
    button_background_color: "#C9FBFE",
    button_gradient_mode: false,
    button_gradient_end_color: "#7461FD",
    card_glassmorphic: false,
    primary_text_color: "#000000",
    secondary_text_color: "#2847AE",
    button_text_color: "#ffffff",
    start_playing_button_text: "Start Playing",
    continue_to_game_button_text: "Continue to Game",
    player_title: "",
    player_short_description: "",
    player_detailed_instructions: "",
    email_sender_name: "",
    thank_you_email_subject: "Thank you for playing!",
    thank_you_email_body: "",
    thank_you_email_mode: "editor"
  })
  const [editingCampaign, setEditingCampaign] = useState({
    id: "",
    name: "",
    description: "",
    game_id: "",
    campaign_type: "",
    is_one_time_use: true,
    max_uses: 1,
    location: "",
    expires_at: "",
    enable_customization: false,
    logo_image: "",
    background_wallpaper: "",
    button_color_theme: "#3b82f6",
    button_background_color: "#ec4899",
    button_gradient_mode: false,
    button_gradient_end_color: "#3b82f6",
    card_glassmorphic: false,
    primary_text_color: "#ffffff",
    secondary_text_color: "#e4e4e7",
    button_text_color: "#ffffff",
    start_playing_button_text: "Start Playing",
    continue_to_game_button_text: "Continue to Game",
    player_title: "",
    player_short_description: "",
    player_detailed_instructions: "",
    email_sender_name: "",
    thank_you_email_subject: "Thank you for playing!",
    thank_you_email_body: "",
    thank_you_email_mode: "editor"
  })

  // File upload state
  const [newCampaignLogoFile, setNewCampaignLogoFile] = useState<File | null>(null)
  const [newCampaignBackgroundFile, setNewCampaignBackgroundFile] = useState<File | null>(null)
  const [editingCampaignLogoFile, setEditingCampaignLogoFile] = useState<File | null>(null)
  const [editingCampaignBackgroundFile, setEditingCampaignBackgroundFile] = useState<File | null>(null)
  const [originalEmailBody, setOriginalEmailBody] = useState<string>('') // Track original to avoid re-sending large data
  const [originalCampaignValues, setOriginalCampaignValues] = useState<{
    max_uses: number
    current_uses: number
    game_id: string
    game_name: string
  } | null>(null)
  const [uploadPreviews, setUploadPreviews] = useState({
    newLogo: '',
    newBackground: '',
    editLogo: '',
    editBackground: ''
  })
  const [createCustomizationErrorsTouched, setCreateCustomizationErrorsTouched] = useState(false)
  const [editCustomizationErrorsTouched, setEditCustomizationErrorsTouched] = useState(false)
  const [createBasicErrorsTouched, setCreateBasicErrorsTouched] = useState(false)
  const [editBasicErrorsTouched, setEditBasicErrorsTouched] = useState(false)
  const [createValidationMessage, setCreateValidationMessage] = useState('')
  const [editValidationMessage, setEditValidationMessage] = useState('')
  const [showCampaignLimitError, setShowCampaignLimitError] = useState(false)
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false)

  // Reset validation highlight when dialog opens
  useEffect(() => {
    if (isCreateDialogOpen) {
      setCreateCustomizationErrorsTouched(false)
      setCreateBasicErrorsTouched(false)
      setCreateValidationMessage('')
      setShowCampaignLimitError(hasReachedCampaignsLimit())
    }
  }, [isCreateDialogOpen])

  // Show upgrade modal when we have usage data and are at campaign limit (runs after usage loads)
  useEffect(() => {
    if (isCreateDialogOpen && !accountUsageLoading && hasReachedCampaignsLimit()) {
      setShowCampaignLimitError(true)
      setShowUpgradePlanModal(true)
    }
  }, [isCreateDialogOpen, accountUsageLoading, accountUsage])
  useEffect(() => {
    if (isEditDialogOpen) {
      setEditCustomizationErrorsTouched(false)
      setEditBasicErrorsTouched(false)
      setEditValidationMessage('')
    }
  }, [isEditDialogOpen])

  // Load QR campaigns
  useEffect(() => {
    //console.log('QR Codes useEffect - user:', user)
    //console.log('User keys:', user ? Object.keys(user) : 'No user')

    const merchantId = getMerchantId();
    //console.log('Merchant ID from helper:', merchantId)

    // Only load data if user is authenticated and has a merchant ID
    if (user && merchantId) {
      //console.log('User is authenticated with merchant ID, loading data...')
      loadQRCampaigns()
      loadGames()
      // Also load account usage to check for limits
      loadAccountUsage()
    } else {
      //console.log('User not authenticated or missing merchantId')
      //console.log('SessionStorage contents:')
      //console.log('  - id:', sessionStorage.getItem('id'))
      //console.log('  - session_secret exists:', !!sessionStorage.getItem('session_secret'))
      //console.log('  - user_data:', sessionStorage.getItem('user_data'))
      setIsLoading(false)
    }
  }, [user, getMerchantId])

  // Check for ?create=true query parameter to open create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setIsCreateDialogOpen(true)
    }
  }, [searchParams])

  // "Scroll for more fields" hint: measure content-only height so hint visibility can't cause feedback loop
  useEffect(() => {
    if (!isCreateDialogOpen) {
      setCreateDialogShowScrollHint(false)
      setCreateDialogHasOverflow(false)
      return
    }
    setCreateDialogShowScrollHint(true)
    const scrollEl = createDialogScrollRef.current
    const contentEl = createDialogContentRef.current
    if (!scrollEl || !contentEl) return
    let debounceTimer: ReturnType<typeof setTimeout>
    const check = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (!scrollEl || !contentEl) return
        const hasOverflow = contentEl.offsetHeight > scrollEl.clientHeight + 2
        setCreateDialogHasOverflow(hasOverflow)
      }, 100)
    }
    const runCheck = () => {
      if (!scrollEl || !contentEl) return
      const hasOverflow = contentEl.offsetHeight > scrollEl.clientHeight + 2
      setCreateDialogHasOverflow(hasOverflow)
    }
    const t = setTimeout(runCheck, 150)
    const t2 = setTimeout(runCheck, 450)
    const ro = new ResizeObserver(check)
    ro.observe(contentEl)
    return () => {
      clearTimeout(debounceTimer)
      clearTimeout(t)
      clearTimeout(t2)
      ro.disconnect()
    }
  }, [isCreateDialogOpen, newCampaign.enable_customization])

  const handleCreateDialogScroll = () => {
    const el = createDialogScrollRef.current
    if (!el) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setCreateDialogShowScrollHint(false)
  }

  // "Scroll for more fields" hint for Edit dialog: measure content-only to avoid feedback loop
  useEffect(() => {
    if (!isEditDialogOpen) {
      setEditDialogShowScrollHint(false)
      setEditDialogHasOverflow(false)
      return
    }
    setEditDialogShowScrollHint(true)
    const scrollEl = editDialogScrollRef.current
    const contentEl = editDialogContentRef.current
    if (!scrollEl || !contentEl) return
    let debounceTimer: ReturnType<typeof setTimeout>
    const check = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (!scrollEl || !contentEl) return
        const hasOverflow = contentEl.offsetHeight > scrollEl.clientHeight + 2
        setEditDialogHasOverflow(hasOverflow)
      }, 100)
    }
    const runCheck = () => {
      if (!scrollEl || !contentEl) return
      const hasOverflow = contentEl.offsetHeight > scrollEl.clientHeight + 2
      setEditDialogHasOverflow(hasOverflow)
    }
    const t = setTimeout(runCheck, 150)
    const t2 = setTimeout(runCheck, 450)
    const ro = new ResizeObserver(check)
    ro.observe(contentEl)
    return () => {
      clearTimeout(debounceTimer)
      clearTimeout(t)
      clearTimeout(t2)
      ro.disconnect()
    }
  }, [isEditDialogOpen, editingCampaign.enable_customization])

  const handleEditDialogScroll = () => {
    const el = editDialogScrollRef.current
    if (!el) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setEditDialogShowScrollHint(false)
  }

  // Fetch account usage when Create or Edit dialog opens
  useEffect(() => {
    if (!isCreateDialogOpen && !isEditDialogOpen) return
    let cancelled = false
    setAccountUsageLoading(true)
    const minDelayMs = 800
    const delayPromise = new Promise<void>((resolve) => setTimeout(resolve, minDelayMs))
    Promise.all([getAccountUsage(), delayPromise])
      .then(([res]) => {
        if (cancelled) return
        const data = res as AccountUsageData | null | undefined
        if (data && typeof data === 'object' && data.usage) setAccountUsage(data)
      })
      .catch(() => {
        if (!cancelled) setAccountUsage(null)
      })
      .finally(() => {
        if (!cancelled) setAccountUsageLoading(false)
      })
    return () => { cancelled = true }
  }, [isCreateDialogOpen, isEditDialogOpen])

  // Max allowed "maximum uses" from usage API: remaining = limit - capacity (e.g. 500 total, 10 capacity → 490)
  const getMaxUsesCap = (): number => {
    if (!accountUsage?.usage) return 1000
    const item = accountUsage.usage['qr_codes'] ?? accountUsage.usage['customers'] ?? Object.values(accountUsage.usage)[0]
    if (!item || typeof item.limit !== 'number') return 1000
    if (item.limit === -1) return 1000
    const capacity = typeof item.capacity === 'number' ? item.capacity : 0
    const remaining = item.limit - capacity
    return Math.max(1, remaining)
  }

  // Check if campaigns limit has been reached
  const hasReachedCampaignsLimit = (): boolean => {
    if (!accountUsage?.usage) return false
    const campaignsItem = accountUsage.usage['qr_campaigns'] ?? accountUsage.usage['campaigns']
    if (!campaignsItem || typeof campaignsItem.limit !== 'number') return false
    if (campaignsItem.limit === -1) return false // unlimited
    const current = typeof campaignsItem.current === 'number' ? campaignsItem.current : 0
    return current >= campaignsItem.limit
  }

  // Handle Create QR Code button click - check limit first
  const handleCreateQRCodeButtonClick = () => {
    // Open the dialog (limit check will happen inside the dialog)
    setIsCreateDialogOpen(true)
  }

  // Treat rich-text body as empty when it's empty or only empty HTML (e.g. <p></p> from TipTap).
  // Content with images (pasted or inserted) counts as not empty so submission is allowed.
  const isThankYouEmailBodyEmpty = (html: string | undefined): boolean => {
    if (!html || !html.trim()) return true
    if (/<img\s/i.test(html)) return false
    const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    return !stripped
  }

  // Compress image to ensure it's under 1MB (max dimensions, adaptive quality)
  const compressImage = async (file: File, maxWidth = 1200, initialQuality = 0.85): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          } else {
            width = Math.round((width * maxWidth) / height)
            height = maxWidth
          }
        }

        // Compress with adaptive quality to ensure under 1MB
        const tryCompress = (quality: number) => {
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }
          ctx.drawImage(img, 0, 0, width, height)

          // Convert to compressed base64
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality)

          // Check size (base64 length * 0.75 ≈ actual bytes)
          const base64Length = compressedDataUrl.length
          const estimatedSizeBytes = Math.round(base64Length * 0.75)
          const maxSizeBytes = 1 * 1024 * 1024 // 1MB

          if (estimatedSizeBytes > maxSizeBytes && quality > 0.1) {
            // Try lower quality
            tryCompress(Math.max(0.1, quality - 0.1))
          } else {
            resolve(compressedDataUrl)
          }
        }

        tryCompress(initialQuality)
      }
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    })
  }

  // When customization is Yes, all customization fields must be filled before create/update
  const getCreateCustomizationErrors = (): string[] => {
    if (!newCampaign.enable_customization) return []
    const errs: string[] = []
    const hasLogo = !!(newCampaign.logo_image?.trim() || newCampaignLogoFile)
    const hasBackground = !!(newCampaign.background_wallpaper?.trim() || newCampaignBackgroundFile)
    if (!hasLogo) errs.push('Logo image')
    if (!hasBackground) errs.push('Background wallpaper')
    if (!newCampaign.start_playing_button_text?.trim()) errs.push('Start Playing button text')
    if (!newCampaign.continue_to_game_button_text?.trim()) errs.push('Continue to Game button text')
    if (!newCampaign.player_title?.trim()) errs.push('Player title')
    if (!newCampaign.player_short_description?.trim()) errs.push('Player short description')
    if (!newCampaign.player_detailed_instructions?.trim()) errs.push('Player detailed instructions')
    if (!newCampaign.email_sender_name?.trim()) errs.push('Email sender name')
    if (!newCampaign.thank_you_email_subject?.trim()) errs.push('Thank you email subject')
    if (isThankYouEmailBodyEmpty(newCampaign.thank_you_email_body)) errs.push('Thank you email body')
    return errs
  }

  const getEditCustomizationErrors = (): string[] => {
    if (!editingCampaign.enable_customization) return []
    const errs: string[] = []
    const hasLogo = !!(editingCampaign.logo_image?.trim() || editingCampaignLogoFile)
    const hasBackground = !!(editingCampaign.background_wallpaper?.trim() || editingCampaignBackgroundFile)
    if (!hasLogo) errs.push('Logo image')
    if (!hasBackground) errs.push('Background wallpaper')
    if (!editingCampaign.start_playing_button_text?.trim()) errs.push('Start Playing button text')
    if (!editingCampaign.continue_to_game_button_text?.trim()) errs.push('Continue to Game button text')
    if (!editingCampaign.player_title?.trim()) errs.push('Player title')
    if (!editingCampaign.player_short_description?.trim()) errs.push('Player short description')
    if (!editingCampaign.player_detailed_instructions?.trim()) errs.push('Player detailed instructions')
    if (!editingCampaign.email_sender_name?.trim()) errs.push('Email sender name')
    if (!editingCampaign.thank_you_email_subject?.trim()) errs.push('Thank you email subject')
    if (isThankYouEmailBodyEmpty(editingCampaign.thank_you_email_body)) errs.push('Thank you email body')
    return errs
  }

  const loadQRCampaigns = async () => {
    try {
      setIsLoading(true)
      const response = await getQRCampaigns()
      //console.log('QR campaigns response:', response)

      // Handle both array and object responses
      // API returns {success: true, data: [...]} which gets unwrapped to [...]
      // But sometimes returns null or empty object {} when no data
      if (Array.isArray(response)) {
        setCampaigns(response)
      } else if (response === null || (typeof response === 'object' && Object.keys(response).length === 0)) {
        // Null or empty object response - treat as empty array
        setCampaigns([])
      } else {
        console.error('Invalid response format:', response)
        setCampaigns([])
      }
    } catch (error) {
      console.error('Failed to load QR campaigns:', error)
      setCampaigns([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadGames = async () => {
    try {
      // publicGetGames returns the games array directly
      const gamesData = await publicGetGames()
      setGames(Array.isArray(gamesData) ? gamesData : [])
    } catch (error) {
      console.error('Failed to load games:', error)
      setGames([])
    }
  }

  const loadAccountUsage = async () => {
    try {
      const usageData = await getAccountUsage() as AccountUsageData | null | undefined
      if (usageData && typeof usageData === 'object' && usageData.usage) {
        setAccountUsage(usageData)
      }
    } catch (error) {
      console.error('Failed to load account usage:', error)
      // Don't set to null on error - keep existing data if any
    }
  }

  const handleCreateQRCode = async () => {
    try {
      // Check campaigns limit before creating — show upgrade modal
      if (hasReachedCampaignsLimit()) {
        setShowCampaignLimitError(true)
        setShowUpgradePlanModal(true)
        return
      }

      const missingBasic: string[] = []
      if (!newCampaign.name?.trim()) missingBasic.push('Campaign Name')
      if (!newCampaign.game_id) missingBasic.push('Game')
      if (missingBasic.length > 0) {
        setCreateBasicErrorsTouched(true)
        setCreateValidationMessage(`Please fill in required fields: ${missingBasic.join(', ')}`)
        return
      }
      if (newCampaign.enable_customization) {
        const errs = getCreateCustomizationErrors()
        if (errs.length > 0) {
          setCreateCustomizationErrorsTouched(true)
          setCreateValidationMessage(`Please fill in all required customization fields: ${errs.join(', ')}`)
          return
        }
      }
      setCreateValidationMessage('')
      setShowCampaignLimitError(false)
      // Convert files to base64 if they exist
      let logoImageData = newCampaign.logo_image
      let backgroundWallpaperData = newCampaign.background_wallpaper

      if (newCampaignLogoFile) {
        logoImageData = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(newCampaignLogoFile)
        })
      }

      if (newCampaignBackgroundFile) {
        backgroundWallpaperData = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(newCampaignBackgroundFile)
        })
      }

      // Create campaign - this already generates the QR URL
      // Note: createQRCampaign returns unwrapped data object (callApi auto-unwraps {success, data})
      const createResponse = await createQRCampaign({
        name: newCampaign.name,
        description: newCampaign.description,
        gameId: newCampaign.game_id,
        isOneTimeUse: newCampaign.is_one_time_use,
        maxUses: Math.min(newCampaign.max_uses, getMaxUsesCap()),
        campaignType: newCampaign.campaign_type || 'game_promotion',
        location: newCampaign.location,
        expiresAt: newCampaign.expires_at,
        enableCustomization: newCampaign.enable_customization,
        logoImage: newCampaign.enable_customization ? logoImageData : undefined,
        backgroundWallpaper: newCampaign.enable_customization ? backgroundWallpaperData : undefined,
        buttonColorTheme: newCampaign.enable_customization ? newCampaign.button_color_theme : undefined,
        buttonBackgroundColor: newCampaign.enable_customization ? newCampaign.button_background_color : undefined,
        buttonGradientMode: newCampaign.enable_customization ? newCampaign.button_gradient_mode : undefined,
        buttonGradientEndColor: newCampaign.enable_customization && newCampaign.button_gradient_mode ? newCampaign.button_gradient_end_color : undefined,
        cardGlassmorphic: newCampaign.enable_customization ? newCampaign.card_glassmorphic : undefined,
        primaryTextColor: newCampaign.enable_customization ? newCampaign.primary_text_color : undefined,
        secondaryTextColor: newCampaign.enable_customization ? newCampaign.secondary_text_color : undefined,
        buttonTextColor: newCampaign.enable_customization ? newCampaign.button_text_color : undefined,
        startPlayingButtonText: newCampaign.enable_customization ? newCampaign.start_playing_button_text : undefined,
        continueToGameButtonText: newCampaign.enable_customization ? newCampaign.continue_to_game_button_text : undefined,
        playerTitle: newCampaign.enable_customization ? newCampaign.player_title : undefined,
        playerShortDescription: newCampaign.enable_customization ? newCampaign.player_short_description : undefined,
        playerDetailedInstructions: newCampaign.enable_customization ? newCampaign.player_detailed_instructions : undefined,
        emailSenderName: newCampaign.enable_customization ? newCampaign.email_sender_name : undefined,
        thankYouEmailSubject: newCampaign.enable_customization ? newCampaign.thank_you_email_subject : undefined,
        thankYouEmailBody: newCampaign.enable_customization ? newCampaign.thank_you_email_body : undefined,
        thankYouEmailMode: newCampaign.enable_customization ? newCampaign.thank_you_email_mode : undefined
      })

      // createResponse is the unwrapped data object: { id, qr_url, ... }
      if (createResponse?.id) {
        // Campaign created successfully with QR URL already generated
        addToast({
          message: "QR Campaign created successfully!",
          variant: "success",
          duration: 3000
        })
        await loadQRCampaigns()
        setNewCampaign({
          name: "",
          description: "",
          game_id: "",
          campaign_type: "",
          is_one_time_use: true,
          max_uses: 1,
          location: "",
          expires_at: "",
          enable_customization: false,
          logo_image: "",
          background_wallpaper: "",
          button_color_theme: "#7461FD",
          button_background_color: "#C9FBFE",
          button_gradient_mode: false,
          button_gradient_end_color: "#7461FD",
          card_glassmorphic: false,
          primary_text_color: "#000000",
          secondary_text_color: "#2847AE",
          button_text_color: "#ffffff",
          start_playing_button_text: "Start Playing",
          continue_to_game_button_text: "Continue to Game",
          player_title: "",
          player_short_description: "",
          player_detailed_instructions: "",
          email_sender_name: "",
          thank_you_email_subject: "Thank you for playing!",
          thank_you_email_body: "",
          thank_you_email_mode: "editor"
        })
        setNewCampaignLogoFile(null)
        setNewCampaignBackgroundFile(null)
        setUploadPreviews(prev => ({ ...prev, newLogo: '', newBackground: '' }))
        setIsCreateDialogOpen(false)
      } else {
        addToast({
          message: createResponse?.message || "Failed to create QR campaign",
          variant: "error",
          duration: 3000
        })
      }
    } catch (error: any) {
      console.error('Failed to create QR code:', error)
      addToast({
        message: error?.message || "Failed to create QR campaign",
        variant: "error",
        duration: 3000
      })
    } finally {
      setIsGeneratingQR(false)
    }
  }

  const handleCopyQRUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      addToast({
        message: "Successfully copied!",
        variant: "success",
        duration: 2000
      })
    }).catch(() => {
      addToast({
        message: "Failed to copy URL",
        variant: "error",
        duration: 3000
      })
    })
  }

  const handleDownloadQR = async (campaign: QRCampaign) => {
    try {
      // Generate QR code image using QR Server API
      // The qr_url is the validation URL that the QR code should encode
      const validationUrl = campaign.qr_url
      if (!validationUrl) {
        addToast({
          message: "No QR URL available for this campaign",
          variant: "error",
          duration: 3000
        })
        return
      }

      // Generate QR code image (300x300 size)
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(validationUrl)}`

      // Fetch the image as blob
      const response = await fetch(qrImageUrl)
      if (!response.ok) {
        throw new Error("Failed to generate QR code")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Create download link and trigger download
      const link = document.createElement('a')
      link.href = url
      link.download = `qr-${campaign.name?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || campaign.id}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the object URL
      window.URL.revokeObjectURL(url)

      addToast({
        message: "QR Code downloaded successfully!",
        variant: "success",
        duration: 2000
      })
    } catch (error) {
      console.error('Failed to download QR code:', error)
      addToast({
        message: "Failed to download QR code",
        variant: "error",
        duration: 3000
      })
    }
  }

  const handleViewQR = (campaign: QRCampaign) => {
    setSelectedCampaign(campaign)
    setIsQRModalOpen(true)
  }

  const handleViewDetails = (campaign: QRCampaign) => {
    setSelectedCampaign(campaign)
    setIsDetailsModalOpen(true)
  }

  const handleEditCampaign = (campaign: QRCampaign) => {
    setSelectedCampaign(campaign)
    // Parse end_date to YYYY-MM-DD format for the date input
    const endDate = campaign.end_date
      ? new Date(campaign.end_date).toISOString().split('T')[0]
      : ''

    // Store original values for validation (max_uses can only increase, game cannot be changed)
    setOriginalCampaignValues({
      max_uses: campaign.max_uses || 1,
      current_uses: campaign.current_uses || 0,
      game_id: campaign.game_id || '',
      game_name: campaign.game_name || ''
    })

    setEditingCampaign({
      id: campaign.id,
      name: campaign.name,
      description: campaign.description || '',
      game_id: campaign.game_id || '',
      campaign_type: campaign.campaign_type || '',
      is_one_time_use: campaign.is_one_time_use || false,
      max_uses: campaign.max_uses || 1,
      location: campaign.location || '',
      expires_at: endDate,
      enable_customization: campaign.enable_customization || false,
      logo_image: campaign.logo_image || '',
      background_wallpaper: campaign.background_wallpaper || '',
      button_color_theme: campaign.button_color_theme || '#3b82f6',
      button_background_color: campaign.button_background_color || '#ec4899',
      button_gradient_mode: campaign.button_gradient_mode || false,
      button_gradient_end_color: campaign.button_gradient_end_color || '#7461FD',
      card_glassmorphic: campaign.card_glassmorphic || false,
      primary_text_color: campaign.primary_text_color || '#ffffff',
      secondary_text_color: campaign.secondary_text_color || '#e4e4e7',
      button_text_color: campaign.button_text_color || '#ffffff',
      start_playing_button_text: campaign.start_playing_button_text || 'Start Playing',
      continue_to_game_button_text: campaign.continue_to_game_button_text || 'Continue to Game',
      player_title: campaign.player_title || '',
      player_short_description: campaign.player_short_description || '',
      player_detailed_instructions: campaign.player_detailed_instructions || '',
      email_sender_name: campaign.email_sender_name || '',
      thank_you_email_subject: campaign.thank_you_email_subject ?? 'Thank you for playing!',
      thank_you_email_body: campaign.thank_you_email_body ?? '',
      thank_you_email_mode: campaign.thank_you_email_mode ?? 'editor'
    })
    // Store original email body to detect changes and avoid re-sending large base64 images
    setOriginalEmailBody(campaign.thank_you_email_body ?? '')
    // Clear file upload states when opening edit dialog
    setEditingCampaignLogoFile(null)
    setEditingCampaignBackgroundFile(null)
    setUploadPreviews(prev => ({
      ...prev,
      editLogo: '',
      editBackground: ''
    }))
    setIsEditDialogOpen(true)
  }

  const handleUpdateCampaign = async () => {
    try {
      if (!editingCampaign.name?.trim()) {
        setEditBasicErrorsTouched(true)
        setEditValidationMessage('Please fill in required field: Campaign Name')
        return
      }
      if (editingCampaign.enable_customization) {
        const errs = getEditCustomizationErrors()
        if (errs.length > 0) {
          setEditCustomizationErrorsTouched(true)
          setEditValidationMessage(`Please fill in all required customization fields: ${errs.join(', ')}`)
          return
        }
      }
      setEditValidationMessage('')
      setIsUpdating(true)

      // Convert files to base64 if they exist
      // Only send image data if a NEW file was selected
      let logoImageData = undefined
      let backgroundWallpaperData = undefined

      if (editingCampaignLogoFile) {
        logoImageData = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(editingCampaignLogoFile)
        })
      }

      if (editingCampaignBackgroundFile) {
        backgroundWallpaperData = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(editingCampaignBackgroundFile)
        })
      }

      // Only send email body if it actually changed (to avoid re-sending large base64 images)
      const emailBodyChanged = editingCampaign.thank_you_email_body !== originalEmailBody

      // Decide when to send image fields:
      // - If a new file was chosen, send its base64.
      // - If the user entered a normal URL (http/https), always send it so backend updates.
      const logoImageFromUrl =
        editingCampaign.logo_image && /^https?:\/\//i.test(editingCampaign.logo_image)
          ? editingCampaign.logo_image
          : undefined
      const backgroundFromUrl =
        editingCampaign.background_wallpaper && /^https?:\/\//i.test(editingCampaign.background_wallpaper)
          ? editingCampaign.background_wallpaper
          : undefined

      const response = await updateQRCampaign(editingCampaign.id, {
        name: editingCampaign.name,
        description: editingCampaign.description,
        location: editingCampaign.location,
        campaignType: editingCampaign.campaign_type || undefined,
        // gameId is not sent - game cannot be changed after creation
        isOneTimeUse: editingCampaign.is_one_time_use,
        maxUses: Math.min(editingCampaign.max_uses, getMaxUsesCap()),
        endDate: editingCampaign.expires_at ? new Date(editingCampaign.expires_at).toISOString() : undefined,
        enableCustomization: editingCampaign.enable_customization,
        // Images: send NEW file base64, or URL if user entered one (even without a new file)
        logoImage:
          editingCampaign.enable_customization && (editingCampaignLogoFile || logoImageFromUrl)
            ? (editingCampaignLogoFile ? logoImageData : logoImageFromUrl)
            : undefined,
        backgroundWallpaper:
          editingCampaign.enable_customization && (editingCampaignBackgroundFile || backgroundFromUrl)
            ? (editingCampaignBackgroundFile ? backgroundWallpaperData : backgroundFromUrl)
            : undefined,
        buttonColorTheme: editingCampaign.enable_customization ? editingCampaign.button_color_theme : undefined,
        buttonBackgroundColor: editingCampaign.enable_customization ? editingCampaign.button_background_color : undefined,
        buttonGradientMode: editingCampaign.enable_customization ? editingCampaign.button_gradient_mode : undefined,
        buttonGradientEndColor: editingCampaign.enable_customization && editingCampaign.button_gradient_mode ? editingCampaign.button_gradient_end_color : undefined,
        cardGlassmorphic: editingCampaign.enable_customization ? editingCampaign.card_glassmorphic : undefined,
        primaryTextColor: editingCampaign.enable_customization ? editingCampaign.primary_text_color : undefined,
        secondaryTextColor: editingCampaign.enable_customization ? editingCampaign.secondary_text_color : undefined,
        buttonTextColor: editingCampaign.enable_customization ? editingCampaign.button_text_color : undefined,
        startPlayingButtonText: editingCampaign.enable_customization ? editingCampaign.start_playing_button_text : undefined,
        continueToGameButtonText: editingCampaign.enable_customization ? editingCampaign.continue_to_game_button_text : undefined,
        playerTitle: editingCampaign.enable_customization ? editingCampaign.player_title : undefined,
        playerShortDescription: editingCampaign.enable_customization ? editingCampaign.player_short_description : undefined,
        playerDetailedInstructions: editingCampaign.enable_customization ? editingCampaign.player_detailed_instructions : undefined,
        emailSenderName: editingCampaign.enable_customization ? editingCampaign.email_sender_name : undefined,
        thankYouEmailSubject: editingCampaign.enable_customization ? editingCampaign.thank_you_email_subject : undefined,
        // Only send email body if it changed (to avoid 413 errors from large base64 images)
        thankYouEmailBody: editingCampaign.enable_customization && emailBodyChanged ? editingCampaign.thank_you_email_body : undefined,
        thankYouEmailMode: editingCampaign.enable_customization ? editingCampaign.thank_you_email_mode : undefined
      })

      // response is the unwrapped data object
      if (response?.id) {
        addToast({
          message: "Campaign updated successfully!",
          variant: "success",
          duration: 3000
        })
        await loadQRCampaigns()
        setIsEditDialogOpen(false)
        setEditingCampaignLogoFile(null)
        setEditingCampaignBackgroundFile(null)
        setUploadPreviews(prev => ({
          ...prev,
          editLogo: '',
          editBackground: ''
        }))
      } else {
        addToast({
          message: response?.message || "Failed to update campaign",
          variant: "error",
          duration: 3000
        })
      }
    } catch (error: any) {
      console.error('Failed to update campaign:', error)
      addToast({
        message: error?.message || "Failed to update campaign",
        variant: "error",
        duration: 3000
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteCampaign = async (id: string) => {
    if (confirm('Are you sure you want to delete this QR campaign?')) {
      try {
        await deleteQRCampaign(id)
        await loadQRCampaigns()
      } catch (error) {
        console.error('Failed to delete campaign:', error)
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
      case "paused":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
      case "expired":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
      case "draft":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const calculateConversionRate = (campaign: QRCampaign) => {
    // We need conversions data - for now using a placeholder calculation
    const uniqueScans = campaign.unique_scans || 0
    const conversions = Math.floor(uniqueScans * 0.3) // Placeholder: 30% conversion
    return uniqueScans > 0 ? Math.round((conversions / uniqueScans) * 100) : 0
  }

  if (isLoading) {
    return (
      <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
        <div className="flex items-center justify-center h-64">
          <IconLoader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">QR Code Campaigns</h2>
          <p className="text-muted-foreground">
            Create and manage QR codes for your gamified marketing campaigns
          </p>
        </div>
        <Button onClick={handleCreateQRCodeButtonClick}>
          <IconPlus className="w-4 h-4 mr-2" />
          Create QR Code
        </Button>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="!max-w-[95vw] !w-[1400px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Create New QR Campaign</DialogTitle>
              <DialogDescription>
                Generate a new QR code for your gamified marketing campaign
              </DialogDescription>
            </DialogHeader>

            {/* Limit Reached Error Message */}
            {showCampaignLimitError && (
              <div className="mx-6 mb-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                    <IconAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <h4 className="font-semibold text-amber-900 dark:text-amber-100">Campaign Limit Reached</h4>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        You've reached the maximum number of QR campaigns for your current plan.
                      </p>
                    </div>
                    <Button asChild size="sm" className="w-fit" onClick={() => setIsCreateDialogOpen(false)}>
                      <Link href="/dashboard/billing?tab=plans" className="inline-flex items-center gap-2">
                        <IconRocket className="w-4 h-4" />
                        Upgrade Plan
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div
              ref={createDialogScrollRef}
              className="flex-1 overflow-y-auto pr-2"
              onScroll={handleCreateDialogScroll}
            >
              <div ref={createDialogContentRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Information */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="campaign-name">Campaign Name<span className="text-destructive"> *</span></Label>
                        <Input
                          id="campaign-name"
                          placeholder="e.g., Summer Promotion"
                          value={newCampaign.name}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                          className={cn(createBasicErrorsTouched && !newCampaign.name?.trim() && "border-destructive ring-2 ring-destructive/20")}
                        />
                      </div>
                      <div>
                        <Label htmlFor="campaign-description">Description</Label>
                        <Input
                          id="campaign-description"
                          placeholder="Brief description of the campaign"
                          value={newCampaign.description}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="game-select">Select Game<span className="text-destructive"> *</span></Label>
                        <Select value={newCampaign.game_id} onValueChange={(value) => setNewCampaign(prev => ({ ...prev, game_id: value }))}>
                          <SelectTrigger className={cn(createBasicErrorsTouched && !newCampaign.game_id && "border-destructive ring-2 ring-destructive/20")}>
                            <SelectValue placeholder="Choose a game" />
                          </SelectTrigger>
                          <SelectContent>
                            {games.map((game) => (
                              <SelectItem key={game.id} value={game.id}>
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{game.icon}</span>
                                  <span>{game.game_name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="campaign-tags">Campaign Tags (Optional - Max 3, comma-separated)</Label>
                        <div className="space-y-2">
                          <Input
                            id="campaign-tags"
                            placeholder="e.g., promotion, event, seasonal"
                            value={newCampaign.campaign_type}
                            onChange={(e) => {
                              const tags = e.target.value
                              const tagArray = tags.split(',').map(t => t.trim()).filter(t => t)
                              if (tagArray.length <= 3) {
                                setNewCampaign(prev => ({ ...prev, campaign_type: tags }))
                              }
                            }}
                          />
                          {newCampaign.campaign_type && (
                            <div className="flex flex-wrap gap-2">
                              {newCampaign.campaign_type.split(',').map((tag, index) => {
                                const trimmedTag = tag.trim()
                                return trimmedTag ? (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {trimmedTag}
                                  </Badge>
                                ) : null
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="campaign-location">Location (Optional)</Label>
                        <Input
                          id="campaign-location"
                          placeholder="e.g., Front Counter"
                          value={newCampaign.location}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, location: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="campaign-expires">Expiry Date (Optional)</Label>
                        <Input
                          id="campaign-expires"
                          type="date"
                          value={newCampaign.expires_at}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, expires_at: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Usage Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Usage Settings</h3>
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <Label htmlFor="usage-type" className="sm:shrink-0">Usage Type</Label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 min-w-0">
                          <div className="flex items-center gap-4">
                            <Button
                              type="button"
                              variant={newCampaign.is_one_time_use ? "default" : "outline"}
                              onClick={() => setNewCampaign(prev => ({ ...prev, is_one_time_use: true }))}
                              className="flex items-center gap-2"
                            >
                              <IconToggleRight className="w-4 h-4" />
                              One-time Use
                            </Button>
                            <Button
                              type="button"
                              variant={!newCampaign.is_one_time_use ? "default" : "outline"}
                              onClick={() => setNewCampaign(prev => ({ ...prev, is_one_time_use: false }))}
                              className="flex items-center gap-2"
                            >
                              <IconToggleLeft className="w-4 h-4" />
                              Multi-use
                            </Button>
                          </div>
                          <div className="space-y-2 flex-1 w-full min-w-0">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="max-uses" className="text-sm">Maximum Scans</Label>
                              <span className="text-sm tabular-nums text-muted-foreground">
                                {Math.min(Math.max(1, newCampaign.max_uses), getMaxUsesCap())}
                              </span>
                            </div>
                            <Slider
                              id="max-uses"
                              min={1}
                              max={getMaxUsesCap()}
                              step={1}
                              value={[Math.min(Math.max(1, newCampaign.max_uses), getMaxUsesCap())]}
                              onValueChange={([v]) => setNewCampaign(prev => ({ ...prev, max_uses: Math.min(Math.max(1, v ?? 1), getMaxUsesCap()) }))}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 border-0 bg-amber-100 dark:bg-amber-950/40 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200 space-y-1">
                        <p><span className="font-medium">One-time use:</span> Each customer can only scan the QR code once.</p>
                        <p><span className="font-medium">Multi-use:</span> Each customer can scan the QR code many times.</p>
                        <p><span className="font-medium">Maximum Scans:</span> Total number of scans allowed for this campaign. Applies to both types.</p>
                      </div>
                    </div>
                  </div>

                  {/* Account usage on left - only when customization is Yes */}
                  {newCampaign.enable_customization && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Account usage</h3>
                          <Button asChild size="sm" className="shrink-0 upgrade-plan-shine">
                            <Link href="/dashboard/billing?tab=plans" className="relative">
                              <IconRocket className="w-4 h-4 mr-1.5 relative z-10" />
                              <span className="relative z-10">Upgrade plan</span>
                            </Link>
                          </Button>
                        </div>
                        {accountUsageLoading ? (
                          <div className="space-y-3">
                            <Skeleton className="h-3 w-48" />
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <Skeleton className="h-4 w-24" />
                                  <Skeleton className="h-4 w-12" />
                                </div>
                                <Skeleton className="h-2 w-full rounded-full" />
                              </div>
                            ))}
                          </div>
                        ) : accountUsage?.usage ? (
                          <div className="space-y-3">
                            {accountUsage.period && (
                              <p className="text-xs text-muted-foreground">
                                Period: {accountUsage.period.start} – {accountUsage.period.end}
                              </p>
                            )}
                            {Object.entries(accountUsage.usage).map(([key, item]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              const limit = typeof item.limit === 'number' ? item.limit : undefined
                              const current = typeof item.current === 'number' ? item.current : 0
                              const hasValidPercentage = typeof item.percentage === 'number' && item.percentage >= 0
                              const computedPercentage = limit !== undefined && limit > 0 ? (current / limit) * 100 : undefined
                              const showBar = limit !== undefined && limit > 0 && (hasValidPercentage || computedPercentage !== undefined)
                              const barValue = hasValidPercentage ? item.percentage! : (computedPercentage ?? 0)
                              const valueText = limit === -1 || limit === undefined
                                ? `${current}`
                                : `${current} / ${limit}`
                              return (
                                <div key={key} className="space-y-1.5">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-foreground">{label}</span>
                                    <span className="text-muted-foreground tabular-nums">{valueText}{limit === -1 ? ' (unlimited)' : ''}</span>
                                  </div>
                                  {showBar && (
                                    <Progress value={Math.min(100, barValue)} className="h-2" />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No usage data available</p>
                        )}
                      </div>
                      {/* Thank You for Playing email */}
                      <div className="space-y-4 pt-2">
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Thank You for Playing email</h3>
                        <p className="text-sm text-muted-foreground">
                          Customize the email sent to players after they play.
                        </p>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="email-sender-name">Sender Name<span className="text-destructive"> *</span></Label>
                            <Input
                              id="email-sender-name"
                              placeholder="e.g., GameIt"
                              value={newCampaign.email_sender_name}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, email_sender_name: e.target.value }))}
                              className={cn("mt-1", createCustomizationErrorsTouched && !newCampaign.email_sender_name?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                          </div>
                          <div>
                            <Label htmlFor="thank-you-email-subject">Subject<span className="text-destructive"> *</span></Label>
                            <Input
                              id="thank-you-email-subject"
                              placeholder="e.g., Thank you for playing!"
                              value={newCampaign.thank_you_email_subject}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, thank_you_email_subject: e.target.value }))}
                              className={cn("mt-1", createCustomizationErrorsTouched && !newCampaign.thank_you_email_subject?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                          </div>
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <Label htmlFor="thank-you-email-body">Body<span className="text-destructive"> *</span></Label>
                              <div className="flex rounded-md border border-input bg-transparent p-0.5">
                                <Button
                                  type="button"
                                  variant={newCampaign.thank_you_email_mode === "editor" ? "secondary" : "ghost"}
                                  size="sm"
                                  className={cn("h-7 px-2 text-xs", newCampaign.thank_you_email_mode === "editor" && "!bg-foreground !text-background dark:!bg-white dark:!text-black")}
                                  onClick={() => setNewCampaign(prev => ({ ...prev, thank_you_email_mode: "editor" }))}
                                >
                                  Email editor
                                </Button>
                                <Button
                                  type="button"
                                  variant={newCampaign.thank_you_email_mode === "html" ? "secondary" : "ghost"}
                                  size="sm"
                                  className={cn("h-7 px-2 text-xs", newCampaign.thank_you_email_mode === "html" && "!bg-foreground !text-background dark:!bg-white dark:!text-black")}
                                  onClick={() => setNewCampaign(prev => ({ ...prev, thank_you_email_mode: "html" }))}
                                >
                                  Pure HTML
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs bg-yellow-200 dark:bg-yellow-400 text-black px-2 py-1 rounded mb-2 inline-block">
                              ⚠️ Images added to the email body count toward the 50MB total limit. Please keep inline images small.
                            </p>
                            {newCampaign.thank_you_email_mode === "editor" ? (
                              <EmailRichTextEditor
                                id="thank-you-email-body"
                                value={newCampaign.thank_you_email_body}
                                onChange={(html) => setNewCampaign(prev => ({ ...prev, thank_you_email_body: html }))}
                                placeholder="Write your thank you message. Use the toolbar for formatting and images."
                                className={cn("mt-1", createCustomizationErrorsTouched && isThankYouEmailBodyEmpty(newCampaign.thank_you_email_body) && "ring-2 ring-destructive rounded-md")}
                              />
                            ) : (
                              <Textarea
                                id="thank-you-email-body"
                                placeholder="Write HTML for the email body (e.g. <p>Thanks for playing!</p>)"
                                value={newCampaign.thank_you_email_body}
                                onChange={(e) => setNewCampaign(prev => ({ ...prev, thank_you_email_body: e.target.value }))}
                                rows={5}
                                className={cn("mt-1 text-sm font-mono", createCustomizationErrorsTouched && isThankYouEmailBodyEmpty(newCampaign.thank_you_email_body) && "border-destructive ring-2 ring-destructive/20")}
                              />
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {newCampaign.thank_you_email_mode === "html"
                                ? "HTML will be rendered in the email."
                                : "Use the toolbar for bold, italic, lists, links, and images."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Right Column - Customization */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-base font-semibold">
                          Enable Customization
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Customize appearance and messaging for players
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <Button
                          type="button"
                          variant={!newCampaign.enable_customization ? "default" : "outline"}
                          onClick={() => setNewCampaign(prev => ({ ...prev, enable_customization: false }))}
                          className="flex-1"
                        >
                          No
                        </Button>
                        <Button
                          type="button"
                          variant={newCampaign.enable_customization ? "default" : "outline"}
                          onClick={() => setNewCampaign(prev => ({ ...prev, enable_customization: true }))}
                          className="flex-1"
                        >
                          Yes
                        </Button>
                      </div>
                    </div>

                    {/* Account usage below customization - only when customization is No */}
                    {!newCampaign.enable_customization && (
                      <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Account usage</h3>
                          <Button asChild size="sm" className="shrink-0 upgrade-plan-shine">
                            <Link href="/dashboard/billing?tab=plans" className="relative">
                              <IconRocket className="w-4 h-4 mr-1.5 relative z-10" />
                              <span className="relative z-10">Upgrade plan</span>
                            </Link>
                          </Button>
                        </div>
                        {accountUsageLoading ? (
                          <div className="space-y-3">
                            <Skeleton className="h-3 w-48" />
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <Skeleton className="h-4 w-24" />
                                  <Skeleton className="h-4 w-12" />
                                </div>
                                <Skeleton className="h-2 w-full rounded-full" />
                              </div>
                            ))}
                          </div>
                        ) : accountUsage?.usage ? (
                          <div className="space-y-3">
                            {accountUsage.period && (
                              <p className="text-xs text-muted-foreground">
                                Period: {accountUsage.period.start} – {accountUsage.period.end}
                              </p>
                            )}
                            {Object.entries(accountUsage.usage).map(([key, item]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              const limit = typeof item.limit === 'number' ? item.limit : undefined
                              const current = typeof item.current === 'number' ? item.current : 0
                              const hasValidPercentage = typeof item.percentage === 'number' && item.percentage >= 0
                              const computedPercentage = limit !== undefined && limit > 0 ? (current / limit) * 100 : undefined
                              const showBar = limit !== undefined && limit > 0 && (hasValidPercentage || computedPercentage !== undefined)
                              const barValue = hasValidPercentage ? item.percentage! : (computedPercentage ?? 0)
                              const valueText = limit === -1 || limit === undefined
                                ? `${current}`
                                : `${current} / ${limit}`
                              return (
                                <div key={key} className="space-y-1.5">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-foreground">{label}</span>
                                    <span className="text-muted-foreground tabular-nums">{valueText}{limit === -1 ? ' (unlimited)' : ''}</span>
                                  </div>
                                  {showBar && (
                                    <Progress value={Math.min(100, barValue)} className="h-2" />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No usage data available</p>
                        )}
                      </div>
                    )}

                    {newCampaign.enable_customization && (
                      <div className="space-y-4 pt-2">
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Player Customization</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ImageUpload
                            label="Logo Image"
                            url={newCampaign.logo_image}
                            onUrlChange={(url) => setNewCampaign(prev => ({ ...prev, logo_image: url }))}
                            file={newCampaignLogoFile}
                            onFileChange={setNewCampaignLogoFile}
                            preview={uploadPreviews.newLogo}
                            onPreviewChange={(preview) => setUploadPreviews(prev => ({ ...prev, newLogo: preview }))}
                            helpText="Upload your brand logo"
                            required
                            hasError={createCustomizationErrorsTouched && !(newCampaign.logo_image?.trim() || newCampaignLogoFile)}
                          />

                          <ImageUpload
                            label="Background Wallpaper"
                            url={newCampaign.background_wallpaper}
                            onUrlChange={(url) => setNewCampaign(prev => ({ ...prev, background_wallpaper: url }))}
                            file={newCampaignBackgroundFile}
                            onFileChange={setNewCampaignBackgroundFile}
                            preview={uploadPreviews.newBackground}
                            onPreviewChange={(preview) => setUploadPreviews(prev => ({ ...prev, newBackground: preview }))}
                            helpText="Upload background image"
                            aspectRatio="portrait"
                            required
                            hasError={createCustomizationErrorsTouched && !(newCampaign.background_wallpaper?.trim() || newCampaignBackgroundFile)}
                          />
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="button-color-theme">Button Color Theme</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="button-color-theme"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={newCampaign.button_color_theme}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_color_theme: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1"
                                  value={newCampaign.button_color_theme}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_color_theme: e.target.value }))}
                                  placeholder="#3b82f6"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Button and accent color
                              </p>
                            </div>
                            <div>
                              <Label htmlFor="button-background-color">Card color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="button-background-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={newCampaign.button_background_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_background_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1"
                                  value={newCampaign.button_background_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_background_color: e.target.value }))}
                                  placeholder="#ec4899"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Form card on top of the wallpaper
                              </p>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Label>Button Gradient</Label>
                            <div className="flex items-center gap-4 mt-2">
                              <Button
                                type="button"
                                variant={!newCampaign.button_gradient_mode ? "default" : "outline"}
                                onClick={() => setNewCampaign(prev => ({ ...prev, button_gradient_mode: false }))}
                                className="flex-1"
                              >
                                Solid
                              </Button>
                              <Button
                                type="button"
                                variant={newCampaign.button_gradient_mode ? "default" : "outline"}
                                onClick={() => setNewCampaign(prev => ({ ...prev, button_gradient_mode: true }))}
                                className="flex-1"
                              >
                                Gradient
                              </Button>
                            </div>

                            {newCampaign.button_gradient_mode && (
                              <div className="mt-3">
                                <Label htmlFor="button-gradient-end-color">Gradient End Color</Label>
                                <div className="flex gap-2 items-center">
                                  <Input
                                    id="button-gradient-end-color"
                                    type="color"
                                    className="w-20 h-10 p-1 cursor-pointer"
                                    value={newCampaign.button_gradient_end_color}
                                    onChange={(e) => setNewCampaign(prev => ({ ...prev, button_gradient_end_color: e.target.value }))}
                                  />
                                  <Input
                                    type="text"
                                    className="flex-1"
                                    value={newCampaign.button_gradient_end_color}
                                    onChange={(e) => setNewCampaign(prev => ({ ...prev, button_gradient_end_color: e.target.value }))}
                                    placeholder="#7461FD"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mt-3">
                            <Label>Card Effect</Label>
                            <div className="flex items-center gap-4 mt-2">
                              <Button
                                type="button"
                                variant={!newCampaign.card_glassmorphic ? "default" : "outline"}
                                onClick={() => setNewCampaign(prev => ({ ...prev, card_glassmorphic: false }))}
                                className="flex-1"
                              >
                                Solid
                              </Button>
                              <Button
                                type="button"
                                variant={newCampaign.card_glassmorphic ? "default" : "outline"}
                                onClick={() => setNewCampaign(prev => ({ ...prev, card_glassmorphic: true }))}
                                className="flex-1"
                              >
                                Glassmorphic
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {newCampaign.card_glassmorphic
                                ? "Apply frosted glass effect with blur and transparency"
                                : "Standard solid card background"}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="primary-text-color">Primary text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="primary-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={newCampaign.primary_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, primary_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={newCampaign.primary_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, primary_text_color: e.target.value }))}
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="secondary-text-color">Secondary text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="secondary-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={newCampaign.secondary_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, secondary_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={newCampaign.secondary_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, secondary_text_color: e.target.value }))}
                                  placeholder="#e4e4e7"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="button-text-color">Button text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="button-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={newCampaign.button_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={newCampaign.button_text_color}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, button_text_color: e.target.value }))}
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="start-playing-button-text">Start Playing button text<span className="text-destructive"> *</span></Label>
                            <Input
                              id="start-playing-button-text"
                              placeholder="e.g., Start Playing"
                              value={newCampaign.start_playing_button_text}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, start_playing_button_text: e.target.value }))}
                              className={cn(createCustomizationErrorsTouched && !newCampaign.start_playing_button_text?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Text on the registration submit button
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="continue-to-game-button-text">Continue to Game button text<span className="text-destructive"> *</span></Label>
                            <Input
                              id="continue-to-game-button-text"
                              placeholder="e.g., Continue to Game"
                              value={newCampaign.continue_to_game_button_text}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, continue_to_game_button_text: e.target.value }))}
                              className={cn(createCustomizationErrorsTouched && !newCampaign.continue_to_game_button_text?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Text on the button before registration
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="player-title">Title for Players<span className="text-destructive"> *</span></Label>
                            <Input
                              id="player-title"
                              placeholder="e.g., Spin & Win!"
                              value={newCampaign.player_title}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, player_title: e.target.value }))}
                              className={cn(createCustomizationErrorsTouched && !newCampaign.player_title?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Title shown to players
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="player-short-description">Short Description<span className="text-destructive"> *</span></Label>
                            <Input
                              id="player-short-description"
                              placeholder="e.g., Spin the wheel to win exciting prizes!"
                              value={newCampaign.player_short_description}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, player_short_description: e.target.value }))}
                              className={cn(createCustomizationErrorsTouched && !newCampaign.player_short_description?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Brief description for players
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="player-detailed-instructions">Detailed Instructions<span className="text-destructive"> *</span></Label>
                            <Textarea
                              id="player-detailed-instructions"
                              placeholder="e.g., 1. Scan the QR code&#10;2. Spin the wheel&#10;3. Collect your prize"
                              value={newCampaign.player_detailed_instructions}
                              className={cn(createCustomizationErrorsTouched && !newCampaign.player_detailed_instructions?.trim() && "border-destructive ring-2 ring-destructive/20")}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, player_detailed_instructions: e.target.value }))}
                              rows={4}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Step-by-step instructions
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(createDialogHasOverflow || newCampaign.enable_customization) && createDialogShowScrollHint && (
                <div className="sticky bottom-0 left-0 right-0 py-3 flex flex-col items-center pointer-events-none mt-2 bg-black dark:bg-white border-t border-black/20 dark:border-white/20">
                  <span className="text-sm font-medium text-white dark:text-black">Please scroll for more fields</span>
                  <IconChevronDown className="w-5 h-5 text-white dark:text-black animate-bounce mt-1" aria-hidden />
                </div>
              )}
            </div>
            {createValidationMessage && (
              <div className="flex-shrink-0 px-1 py-2 text-sm text-destructive flex items-center gap-2" role="alert">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <IconX className="h-3.5 w-3.5 text-destructive" />
                </span>
                {createValidationMessage}
              </div>
            )}
            <DialogFooter className="flex-shrink-0">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateQRCode}
                disabled={isGeneratingQR}
              >
                {isGeneratingQR ? (
                  <>
                    <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Create QR Code'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Plan Modal — shown when campaign limit reached (z-[100] so it appears on top of Create dialog) */}
        <Dialog open={showUpgradePlanModal} onOpenChange={setShowUpgradePlanModal}>
          <DialogContent className="sm:max-w-md z-[100]">
            <DialogHeader>
              <DialogTitle>Upgrade your plan</DialogTitle>
              <DialogDescription>
                You've reached your active campaign limit. Upgrade your plan to create more QR campaigns.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-2">
              <p className="text-sm text-muted-foreground">
                Your current plan allows a limited number of QR campaigns. Upgrade to unlock more campaigns and grow your gamified marketing.
              </p>
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setShowUpgradePlanModal(false)}
              >
                Maybe later
              </Button>
              <Button asChild className="upgrade-plan-shine">
                <Link href="/dashboard/billing?tab=plans" className="inline-flex items-center gap-2">
                  <IconRocket className="w-4 h-4" />
                  Upgrade plan
                </Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="!max-w-[95vw] !w-[1400px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Edit QR Campaign</DialogTitle>
              <DialogDescription>
                Update the QR campaign settings
              </DialogDescription>
            </DialogHeader>
            <div
              ref={editDialogScrollRef}
              className="flex-1 overflow-y-auto pr-2"
              onScroll={handleEditDialogScroll}
            >
              <div ref={editDialogContentRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Information */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit-campaign-name">Campaign Name<span className="text-destructive"> *</span></Label>
                        <Input
                          id="edit-campaign-name"
                          value={editingCampaign.name}
                          onChange={(e) => setEditingCampaign(prev => ({ ...prev, name: e.target.value }))}
                          className={cn(editBasicErrorsTouched && !editingCampaign.name?.trim() && "border-destructive ring-2 ring-destructive/20")}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-campaign-description">Description</Label>
                        <Input
                          id="edit-campaign-description"
                          value={editingCampaign.description}
                          onChange={(e) => setEditingCampaign(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-game-select">Select Game</Label>
                        <Select value={editingCampaign.game_id} onValueChange={(value) => setEditingCampaign(prev => ({ ...prev, game_id: value }))} disabled>
                          <SelectTrigger className="bg-muted">
                            <SelectValue placeholder="Choose a game" />
                          </SelectTrigger>
                          <SelectContent>
                            {games.map((game) => (
                              <SelectItem key={game.id} value={game.id}>
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{game.icon}</span>
                                  <span>{game.game_name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-1.5 text-xs text-muted-foreground flex items-center gap-1">
                          <IconAlertCircle className="w-3 h-3" />
                          Game cannot be changed after campaign creation
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="edit-campaign-tags">Campaign Tags (Optional - Max 3, comma-separated)</Label>
                        <div className="space-y-2">
                          <Input
                            id="edit-campaign-tags"
                            placeholder="e.g., promotion, event, seasonal"
                            value={editingCampaign.campaign_type}
                            onChange={(e) => {
                              const tags = e.target.value
                              const tagArray = tags.split(',').map(t => t.trim()).filter(t => t)
                              if (tagArray.length <= 3) {
                                setEditingCampaign(prev => ({ ...prev, campaign_type: tags }))
                              }
                            }}
                          />
                          {editingCampaign.campaign_type && (
                            <div className="flex flex-wrap gap-2">
                              {editingCampaign.campaign_type.split(',').map((tag, index) => {
                                const trimmedTag = tag.trim()
                                return trimmedTag ? (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {trimmedTag}
                                  </Badge>
                                ) : null
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="edit-campaign-location">Location (Optional)</Label>
                        <Input
                          id="edit-campaign-location"
                          placeholder="e.g., Front Counter"
                          value={editingCampaign.location}
                          onChange={(e) => setEditingCampaign(prev => ({ ...prev, location: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-campaign-expires">Expiry Date (Optional)</Label>
                        <Input
                          id="edit-campaign-expires"
                          type="date"
                          value={editingCampaign.expires_at}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setEditingCampaign(prev => ({ ...prev, expires_at: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Usage Settings */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Usage Settings</h3>
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <Label htmlFor="edit-usage-type" className="sm:shrink-0">Usage Type</Label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 min-w-0">
                          <div className="flex items-center gap-4">
                            <Button
                              type="button"
                              variant={editingCampaign.is_one_time_use ? "default" : "outline"}
                              onClick={() => setEditingCampaign(prev => ({ ...prev, is_one_time_use: true }))}
                              className="flex items-center gap-2"
                            >
                              <IconToggleRight className="w-4 h-4" />
                              One-time Use
                            </Button>
                            <Button
                              type="button"
                              variant={!editingCampaign.is_one_time_use ? "default" : "outline"}
                              onClick={() => setEditingCampaign(prev => ({ ...prev, is_one_time_use: false }))}
                              className="flex items-center gap-2"
                            >
                              <IconToggleLeft className="w-4 h-4" />
                              Multi-use
                            </Button>
                          </div>
                          <div className="space-y-2 flex-1 w-full min-w-0">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="edit-max-uses" className="text-sm">Maximum Scans</Label>
                              <span className="text-sm tabular-nums text-muted-foreground">
                                {Math.min(Math.max(originalCampaignValues?.max_uses || 1, editingCampaign.max_uses), getMaxUsesCap())}
                              </span>
                            </div>
                            <Slider
                              id="edit-max-uses"
                              min={originalCampaignValues ? Math.max(originalCampaignValues.max_uses, originalCampaignValues.current_uses) : 1}
                              max={getMaxUsesCap()}
                              step={1}
                              value={[Math.min(Math.max(originalCampaignValues?.max_uses || 1, editingCampaign.max_uses), getMaxUsesCap())]}
                              onValueChange={([v]) => setEditingCampaign(prev => ({ ...prev, max_uses: v ?? 1 }))}
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Can only be increased.</span>
                              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 dark:bg-primary/20 dark:border-primary/30">
                                <IconAlertCircle className="w-3 h-3 text-primary" />
                                <span className="text-xs font-medium text-primary">{originalCampaignValues?.current_uses || 0} scanned</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 border-0 bg-amber-100 dark:bg-amber-950/40 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-200 space-y-1">
                        <p><span className="font-medium">One-time use:</span> Each customer can only scan the QR code once.</p>
                        <p><span className="font-medium">Multi-use:</span> Each customer can scan the QR code many times.</p>
                        <p><span className="font-medium">Maximum Scans:</span> Total number of scans allowed for this campaign. Applies to both types.</p>
                      </div>
                    </div>
                  </div>

                  {/* Account usage on left - only when customization is Yes */}
                  {editingCampaign.enable_customization && (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Account usage</h3>
                          <Button asChild size="sm" className="shrink-0 upgrade-plan-shine">
                            <Link href="/dashboard/billing?tab=plans" className="relative">
                              <IconRocket className="w-4 h-4 mr-1.5 relative z-10" />
                              <span className="relative z-10">Upgrade plan</span>
                            </Link>
                          </Button>
                        </div>
                        {accountUsageLoading ? (
                          <div className="space-y-3">
                            <Skeleton className="h-3 w-48" />
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <Skeleton className="h-4 w-24" />
                                  <Skeleton className="h-4 w-12" />
                                </div>
                                <Skeleton className="h-2 w-full rounded-full" />
                              </div>
                            ))}
                          </div>
                        ) : accountUsage?.usage ? (
                          <div className="space-y-3">
                            {accountUsage.period && (
                              <p className="text-xs text-muted-foreground">
                                Period: {accountUsage.period.start} – {accountUsage.period.end}
                              </p>
                            )}
                            {Object.entries(accountUsage.usage).map(([key, item]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              const limit = typeof item.limit === 'number' ? item.limit : undefined
                              const current = typeof item.current === 'number' ? item.current : 0
                              const hasValidPercentage = typeof item.percentage === 'number' && item.percentage >= 0
                              const computedPercentage = limit !== undefined && limit > 0 ? (current / limit) * 100 : undefined
                              const showBar = limit !== undefined && limit > 0 && (hasValidPercentage || computedPercentage !== undefined)
                              const barValue = hasValidPercentage ? item.percentage! : (computedPercentage ?? 0)
                              const valueText = limit === -1 || limit === undefined
                                ? `${current}`
                                : `${current} / ${limit}`
                              return (
                                <div key={key} className="space-y-1.5">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-foreground">{label}</span>
                                    <span className="text-muted-foreground tabular-nums">{valueText}{limit === -1 ? ' (unlimited)' : ''}</span>
                                  </div>
                                  {showBar && (
                                    <Progress value={Math.min(100, barValue)} className="h-2" />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No usage data available</p>
                        )}
                      </div>
                      {/* Thank You for Playing email */}
                      <div className="space-y-4 pt-2">
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Thank You for Playing email</h3>
                        <p className="text-sm text-muted-foreground">
                          Customize the email sent to players after they play.
                        </p>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="edit-email-sender-name">Sender Name<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-email-sender-name"
                              placeholder="e.g., GameIt"
                              value={editingCampaign.email_sender_name}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, email_sender_name: e.target.value }))}
                              className={cn("mt-1", editCustomizationErrorsTouched && !editingCampaign.email_sender_name?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-thank-you-email-subject">Subject<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-thank-you-email-subject"
                              placeholder="e.g., Thank you for playing!"
                              value={editingCampaign.thank_you_email_subject}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, thank_you_email_subject: e.target.value }))}
                              className={cn("mt-1", editCustomizationErrorsTouched && !editingCampaign.thank_you_email_subject?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                          </div>
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <Label htmlFor="edit-thank-you-email-body">Body<span className="text-destructive"> *</span></Label>
                              <div className="flex rounded-md border border-input bg-transparent p-0.5">
                                <Button
                                  type="button"
                                  variant={editingCampaign.thank_you_email_mode === "editor" ? "secondary" : "ghost"}
                                  size="sm"
                                  className={cn("h-7 px-2 text-xs", editingCampaign.thank_you_email_mode === "editor" && "!bg-foreground !text-background dark:!bg-white dark:!text-black")}
                                  onClick={() => setEditingCampaign(prev => ({ ...prev, thank_you_email_mode: "editor" }))}
                                >
                                  Email editor
                                </Button>
                                <Button
                                  type="button"
                                  variant={editingCampaign.thank_you_email_mode === "html" ? "secondary" : "ghost"}
                                  size="sm"
                                  className={cn("h-7 px-2 text-xs", editingCampaign.thank_you_email_mode === "html" && "!bg-foreground !text-background dark:!bg-white dark:!text-black")}
                                  onClick={() => setEditingCampaign(prev => ({ ...prev, thank_you_email_mode: "html" }))}
                                >
                                  Pure HTML
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs bg-yellow-200 dark:bg-yellow-400 text-black px-2 py-1 rounded mb-2 inline-block">
                              ⚠️ Images added to the email body count toward the 50MB total limit. Please keep inline images small.
                            </p>
                            {editingCampaign.thank_you_email_mode === "editor" ? (
                              <EmailRichTextEditor
                                id="edit-thank-you-email-body"
                                value={editingCampaign.thank_you_email_body}
                                onChange={(html) => setEditingCampaign(prev => ({ ...prev, thank_you_email_body: html }))}
                                placeholder="Write your thank you message. Use the toolbar for formatting and images."
                                className={cn("mt-1", editCustomizationErrorsTouched && isThankYouEmailBodyEmpty(editingCampaign.thank_you_email_body) && "ring-2 ring-destructive rounded-md")}
                              />
                            ) : (
                              <Textarea
                                id="edit-thank-you-email-body"
                                placeholder="Write HTML for the email body (e.g. <p>Thanks for playing!</p>)"
                                value={editingCampaign.thank_you_email_body}
                                onChange={(e) => setEditingCampaign(prev => ({ ...prev, thank_you_email_body: e.target.value }))}
                                rows={5}
                                className={cn("mt-1 text-sm font-mono", editCustomizationErrorsTouched && isThankYouEmailBodyEmpty(editingCampaign.thank_you_email_body) && "border-destructive ring-2 ring-destructive/20")}
                              />
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {editingCampaign.thank_you_email_mode === "html"
                                ? "HTML will be rendered in the email."
                                : "Use the toolbar for bold, italic, lists, links, and images."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Right Column - Customization */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="space-y-0.5">
                        <Label className="text-base font-semibold">
                          Enable Customization
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Customize appearance and messaging for players
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <Button
                          type="button"
                          variant={!editingCampaign.enable_customization ? "default" : "outline"}
                          onClick={() => setEditingCampaign(prev => ({ ...prev, enable_customization: false }))}
                          className="flex-1"
                        >
                          No
                        </Button>
                        <Button
                          type="button"
                          variant={editingCampaign.enable_customization ? "default" : "outline"}
                          onClick={() => setEditingCampaign(prev => ({ ...prev, enable_customization: true }))}
                          className="flex-1"
                        >
                          Yes
                        </Button>
                      </div>
                    </div>

                    {/* Account usage below customization - only when customization is No */}
                    {!editingCampaign.enable_customization && (
                      <div className="space-y-4 pt-2">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Account usage</h3>
                          <Button asChild size="sm" className="shrink-0 upgrade-plan-shine">
                            <Link href="/dashboard/billing?tab=plans" className="relative">
                              <IconRocket className="w-4 h-4 mr-1.5 relative z-10" />
                              <span className="relative z-10">Upgrade plan</span>
                            </Link>
                          </Button>
                        </div>
                        {accountUsageLoading ? (
                          <div className="space-y-3">
                            <Skeleton className="h-3 w-48" />
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <Skeleton className="h-4 w-24" />
                                  <Skeleton className="h-4 w-12" />
                                </div>
                                <Skeleton className="h-2 w-full rounded-full" />
                              </div>
                            ))}
                          </div>
                        ) : accountUsage?.usage ? (
                          <div className="space-y-3">
                            {accountUsage.period && (
                              <p className="text-xs text-muted-foreground">
                                Period: {accountUsage.period.start} – {accountUsage.period.end}
                              </p>
                            )}
                            {Object.entries(accountUsage.usage).map(([key, item]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              const limit = typeof item.limit === 'number' ? item.limit : undefined
                              const current = typeof item.current === 'number' ? item.current : 0
                              const hasValidPercentage = typeof item.percentage === 'number' && item.percentage >= 0
                              const computedPercentage = limit !== undefined && limit > 0 ? (current / limit) * 100 : undefined
                              const showBar = limit !== undefined && limit > 0 && (hasValidPercentage || computedPercentage !== undefined)
                              const barValue = hasValidPercentage ? item.percentage! : (computedPercentage ?? 0)
                              const valueText = limit === -1 || limit === undefined
                                ? `${current}`
                                : `${current} / ${limit}`
                              return (
                                <div key={key} className="space-y-1.5">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-foreground">{label}</span>
                                    <span className="text-muted-foreground tabular-nums">{valueText}{limit === -1 ? ' (unlimited)' : ''}</span>
                                  </div>
                                  {showBar && (
                                    <Progress value={Math.min(100, barValue)} className="h-2" />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No usage data available</p>
                        )}
                      </div>
                    )}

                    {editingCampaign.enable_customization && (
                      <div className="space-y-4 pt-2">
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Player Customization</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ImageUpload
                            label="Logo Image"
                            url={editingCampaign.logo_image}
                            onUrlChange={(url) => setEditingCampaign(prev => ({ ...prev, logo_image: url }))}
                            file={editingCampaignLogoFile}
                            onFileChange={setEditingCampaignLogoFile}
                            preview={uploadPreviews.editLogo}
                            onPreviewChange={(preview) => setUploadPreviews(prev => ({ ...prev, editLogo: preview }))}
                            helpText="Upload your brand logo"
                            required
                            hasError={editCustomizationErrorsTouched && !(editingCampaign.logo_image?.trim() || editingCampaignLogoFile)}
                          />

                          <ImageUpload
                            label="Background Wallpaper"
                            url={editingCampaign.background_wallpaper}
                            onUrlChange={(url) => setEditingCampaign(prev => ({ ...prev, background_wallpaper: url }))}
                            file={editingCampaignBackgroundFile}
                            onFileChange={setEditingCampaignBackgroundFile}
                            preview={uploadPreviews.editBackground}
                            onPreviewChange={(preview) => setUploadPreviews(prev => ({ ...prev, editBackground: preview }))}
                            helpText="Upload background image"
                            aspectRatio="portrait"
                            required
                            hasError={editCustomizationErrorsTouched && !(editingCampaign.background_wallpaper?.trim() || editingCampaignBackgroundFile)}
                          />
                        </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="edit-button-color-theme">Button Color Theme</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="edit-button-color-theme"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={editingCampaign.button_color_theme}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_color_theme: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1"
                                  value={editingCampaign.button_color_theme}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_color_theme: e.target.value }))}
                                  placeholder="#3b82f6"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Button and accent color
                              </p>
                            </div>
                            <div>
                              <Label htmlFor="edit-button-background-color">Card color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="edit-button-background-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={editingCampaign.button_background_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_background_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1"
                                  value={editingCampaign.button_background_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_background_color: e.target.value }))}
                                  placeholder="#ec4899"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Form card on top of the wallpaper
                              </p>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Label>Button Gradient</Label>
                            <div className="flex items-center gap-4 mt-2">
                              <Button
                                type="button"
                                variant={!editingCampaign.button_gradient_mode ? "default" : "outline"}
                                onClick={() => setEditingCampaign(prev => ({ ...prev, button_gradient_mode: false }))}
                                className="flex-1"
                              >
                                Solid
                              </Button>
                              <Button
                                type="button"
                                variant={editingCampaign.button_gradient_mode ? "default" : "outline"}
                                onClick={() => setEditingCampaign(prev => ({ ...prev, button_gradient_mode: true }))}
                                className="flex-1"
                              >
                                Gradient
                              </Button>
                            </div>

                            {editingCampaign.button_gradient_mode && (
                              <div className="mt-3">
                                <Label htmlFor="edit-button-gradient-end-color">Gradient End Color</Label>
                                <div className="flex gap-2 items-center">
                                  <Input
                                    id="edit-button-gradient-end-color"
                                    type="color"
                                    className="w-20 h-10 p-1 cursor-pointer"
                                    value={editingCampaign.button_gradient_end_color}
                                    onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_gradient_end_color: e.target.value }))}
                                  />
                                  <Input
                                    type="text"
                                    className="flex-1"
                                    value={editingCampaign.button_gradient_end_color}
                                    onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_gradient_end_color: e.target.value }))}
                                    placeholder="#7461FD"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mt-3">
                            <Label>Card Effect</Label>
                            <div className="flex items-center gap-4 mt-2">
                              <Button
                                type="button"
                                variant={!editingCampaign.card_glassmorphic ? "default" : "outline"}
                                onClick={() => setEditingCampaign(prev => ({ ...prev, card_glassmorphic: false }))}
                                className="flex-1"
                              >
                                Solid
                              </Button>
                              <Button
                                type="button"
                                variant={editingCampaign.card_glassmorphic ? "default" : "outline"}
                                onClick={() => setEditingCampaign(prev => ({ ...prev, card_glassmorphic: true }))}
                                className="flex-1"
                              >
                                Glassmorphic
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {editingCampaign.card_glassmorphic
                                ? "Apply frosted glass effect with blur and transparency"
                                : "Standard solid card background"}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="edit-primary-text-color">Primary text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="edit-primary-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={editingCampaign.primary_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, primary_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={editingCampaign.primary_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, primary_text_color: e.target.value }))}
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="edit-secondary-text-color">Secondary text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="edit-secondary-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={editingCampaign.secondary_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, secondary_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={editingCampaign.secondary_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, secondary_text_color: e.target.value }))}
                                  placeholder="#e4e4e7"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="edit-button-text-color">Button text color</Label>
                              <div className="flex gap-2 items-center">
                                <Input
                                  id="edit-button-text-color"
                                  type="color"
                                  className="w-20 h-10 p-1 cursor-pointer"
                                  value={editingCampaign.button_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_text_color: e.target.value }))}
                                />
                                <Input
                                  type="text"
                                  className="flex-1 min-w-0"
                                  value={editingCampaign.button_text_color}
                                  onChange={(e) => setEditingCampaign(prev => ({ ...prev, button_text_color: e.target.value }))}
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="edit-start-playing-button-text">Start Playing button text<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-start-playing-button-text"
                              placeholder="e.g., Start Playing"
                              value={editingCampaign.start_playing_button_text}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, start_playing_button_text: e.target.value }))}
                              className={cn(editCustomizationErrorsTouched && !editingCampaign.start_playing_button_text?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Text on the registration submit button
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="edit-continue-to-game-button-text">Continue to Game button text<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-continue-to-game-button-text"
                              placeholder="e.g., Continue to Game"
                              value={editingCampaign.continue_to_game_button_text}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, continue_to_game_button_text: e.target.value }))}
                              className={cn(editCustomizationErrorsTouched && !editingCampaign.continue_to_game_button_text?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Text on the button before registration
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="edit-player-title">Title for Players<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-player-title"
                              placeholder="e.g., Spin & Win!"
                              value={editingCampaign.player_title}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, player_title: e.target.value }))}
                              className={cn(editCustomizationErrorsTouched && !editingCampaign.player_title?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Title shown to players
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="edit-player-short-description">Short Description<span className="text-destructive"> *</span></Label>
                            <Input
                              id="edit-player-short-description"
                              placeholder="e.g., Spin the wheel to win exciting prizes!"
                              value={editingCampaign.player_short_description}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, player_short_description: e.target.value }))}
                              className={cn(editCustomizationErrorsTouched && !editingCampaign.player_short_description?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Brief description for players
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="edit-player-detailed-instructions">Detailed Instructions<span className="text-destructive"> *</span></Label>
                            <Textarea
                              id="edit-player-detailed-instructions"
                              placeholder="e.g., 1. Scan the QR code&#10;2. Spin the wheel&#10;3. Collect your prize"
                              value={editingCampaign.player_detailed_instructions}
                              onChange={(e) => setEditingCampaign(prev => ({ ...prev, player_detailed_instructions: e.target.value }))}
                              rows={4}
                              className={cn(editCustomizationErrorsTouched && !editingCampaign.player_detailed_instructions?.trim() && "border-destructive ring-2 ring-destructive/20")}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Step-by-step instructions
                            </p>
                          </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(editDialogHasOverflow || editingCampaign.enable_customization) && editDialogShowScrollHint && (
                <div className="sticky bottom-0 left-0 right-0 py-3 flex flex-col items-center pointer-events-none mt-2 bg-black dark:bg-white border-t border-black/20 dark:border-white/20">
                  <span className="text-sm font-medium text-white dark:text-black">Please scroll for more fields</span>
                  <IconChevronDown className="w-5 h-5 text-white dark:text-black animate-bounce mt-1" aria-hidden />
                </div>
              )}
            </div>
            {editValidationMessage && (
              <div className="flex-shrink-0 px-1 py-2 text-sm text-destructive flex items-center gap-2" role="alert">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <IconX className="h-3.5 w-3.5 text-destructive" />
                </span>
                {editValidationMessage}
              </div>
            )}
            <DialogFooter className="flex-shrink-0">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateCampaign}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Campaign'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active QR Codes</CardTitle>
            <IconQrcode className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.filter(c => c.status === "active").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Total campaigns created
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Scans</CardTitle>
            <IconUsers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.reduce((sum, c) => sum + (c.total_scans || 0), 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {campaigns.reduce((sum, c) => sum + (c.unique_scans || 0), 0).toLocaleString()} unique scans
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">QR Codes Used</CardTitle>
            <IconCircleCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.reduce((sum, c) => sum + (c.current_uses || 0), 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Total usage count
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Usage Rate</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.length > 0
                ? Math.round(
                  campaigns.reduce((sum, c) => sum + calculateConversionRate(c), 0) / campaigns.length
                )
                : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Based on available data
            </p>
          </CardContent>
        </Card>
      </div>

      {/* QR Codes Table */}
      <Card>
        <CardHeader>
          <CardTitle>QR Code Campaigns</CardTitle>
          <CardDescription>
            Manage your QR codes and track their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Max scans</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{campaign.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(campaign.campaign_type || 'QR Campaign')
                          .split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((type, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[100px] space-y-1.5">
                      {campaign.max_uses === 0 ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium">Unlimited</span>
                          <span className="text-xs text-muted-foreground">
                            {campaign.current_uses ?? campaign.total_scans ?? 0} used
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between text-xs">
                            <span className="font-medium">{campaign.current_uses ?? campaign.total_scans ?? 0} used</span>
                            <span className="text-muted-foreground">{campaign.max_uses} total</span>
                          </div>
                          <Progress
                            value={Math.min(100, Math.round(((campaign.current_uses ?? campaign.total_scans ?? 0) / (campaign.max_uses ?? 1)) * 100))}
                            className="h-2"
                          />
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(campaign.status)}>
                      {campaign.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {formatDate(campaign.created_at)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const isExpired =
                        campaign.status === "expired" ||
                        (campaign.end_date && new Date(campaign.end_date) < new Date())
                      return (
                        <div className="flex flex-col gap-1">
                          <Badge
                            className={
                              isExpired
                                ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300 w-fit"
                                : "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 w-fit"
                            }
                          >
                            {isExpired ? "Expired" : "Active"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {campaign.end_date
                              ? formatDate(campaign.end_date)
                              : "No expiry"}
                          </span>
                        </div>
                      )
                    })()}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <IconDots className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleCopyQRUrl(campaign.qr_url || '')}>
                          <IconCopy className="mr-2 h-4 w-4" />
                          Copy URL
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewQR(campaign)}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View QR
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadQR(campaign)}>
                          <IconDownload className="mr-2 h-4 w-4" />
                          Download QR
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleViewDetails(campaign)}>
                          <IconCircleCheck className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditCampaign(campaign)}>
                          <IconEdit className="mr-2 h-4 w-4" />
                          Edit Campaign
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="text-red-600"
                        >
                          <IconTrash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* QR Preview Modal */}
      <Dialog open={isQRModalOpen} onOpenChange={setIsQRModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code Preview</DialogTitle>
            <DialogDescription>
              Scan this QR code to validate and access the campaign
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="bg-white p-4 rounded-lg border">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedCampaign.qr_url || '')}`}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>
              <div className="text-center space-y-1">
                <p className="font-semibold text-lg">{selectedCampaign.name}</p>
                <p className="text-sm text-muted-foreground">{selectedCampaign.description}</p>
              </div>
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCopyQRUrl(selectedCampaign.qr_url || '')}
                >
                  <IconCopy className="mr-2 h-4 w-4" />
                  Copy URL
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    handleDownloadQR(selectedCampaign)
                    setIsQRModalOpen(false)
                  }}
                >
                  <IconDownload className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Details</DialogTitle>
            <DialogDescription>
              Full information about this QR campaign
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Campaign Name</p>
                    <p className="font-medium">{selectedCampaign.name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge className={getStatusColor(selectedCampaign.status)}>
                      {selectedCampaign.status}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Description</p>
                    <p className="font-medium">{selectedCampaign.description || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* QR Settings */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">QR Settings</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Usage Type</p>
                    <p className="font-medium">
                      {selectedCampaign.is_one_time_use ? 'One-time Use' : 'Multi-use'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Scans</p>
                    <p className="font-medium">
                      {selectedCampaign.max_uses === 0 ? 'Unlimited' : selectedCampaign.max_uses}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Current Scans</p>
                    <p className="font-medium">{selectedCampaign.current_uses || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Remaining</p>
                    <p className="font-medium">
                      {selectedCampaign.max_uses === 0
                        ? 'Unlimited'
                        : Math.max(0, (selectedCampaign.max_uses || 0) - (selectedCampaign.current_uses || 0))
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Dates</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">{formatDate(selectedCampaign.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Expires</p>
                    <p className="font-medium">
                      {selectedCampaign.end_date ? formatDate(selectedCampaign.end_date) : 'No expiry'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Statistics</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="text-muted-foreground text-xs">Total Scans</p>
                    <p className="text-2xl font-bold">{selectedCampaign.total_scans || 0}</p>
                  </div>
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="text-muted-foreground text-xs">Unique Scans</p>
                    <p className="text-2xl font-bold">{selectedCampaign.unique_scans || 0}</p>
                  </div>
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="text-muted-foreground text-xs">Participants</p>
                    <p className="text-2xl font-bold">{selectedCampaign.total_participants || 0}</p>
                  </div>
                </div>
              </div>

              {/* QR URL */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">QR URL</h3>
                <div className="flex gap-2">
                  <Input
                    value={selectedCampaign.qr_url || ''}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopyQRUrl(selectedCampaign.qr_url || '')}
                  >
                    <IconCopy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Game Info */}
              {selectedCampaign.game_id && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Linked Game</h3>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-2xl">{selectedCampaign.game_icon || '🎮'}</span>
                    <div>
                      <p className="font-medium">{selectedCampaign.game_name}</p>
                      <p className="text-muted-foreground text-xs">Game ID: {selectedCampaign.game_id}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customization Settings */}
              {selectedCampaign.enable_customization && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Player Customization</h3>
                  <div className="space-y-3 text-sm">
                    {selectedCampaign.logo_image && (
                      <div>
                        <p className="text-muted-foreground">Logo Image</p>
                        <div className="flex items-center gap-2">
                          <img
                            src={selectedCampaign.logo_image}
                            alt="Logo"
                            className="h-12 w-auto object-contain border rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                          <a
                            href={selectedCampaign.logo_image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View URL
                          </a>
                        </div>
                      </div>
                    )}

                    {selectedCampaign.background_wallpaper && (
                      <div>
                        <p className="text-muted-foreground">Background Wallpaper</p>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-12 border rounded overflow-hidden">
                            <img
                              src={selectedCampaign.background_wallpaper}
                              alt="Background"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          </div>
                          <a
                            href={selectedCampaign.background_wallpaper}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View URL
                          </a>
                        </div>
                      </div>
                    )}

                    {selectedCampaign.button_color_theme && (
                      <div className="flex items-center gap-2">
                        <p className="text-muted-foreground">Button Color Theme:</p>
                        <div
                          className="w-8 h-8 rounded border"
                          style={{ backgroundColor: selectedCampaign.button_color_theme }}
                        />
                        <span className="font-mono text-xs">{selectedCampaign.button_color_theme}</span>
                      </div>
                    )}

                    {selectedCampaign.player_title && (
                      <div>
                        <p className="text-muted-foreground">Player Title</p>
                        <p className="font-medium">{selectedCampaign.player_title}</p>
                      </div>
                    )}

                    {selectedCampaign.player_short_description && (
                      <div>
                        <p className="text-muted-foreground">Player Short Description</p>
                        <p className="font-medium">{selectedCampaign.player_short_description}</p>
                      </div>
                    )}

                    {selectedCampaign.player_detailed_instructions && (
                      <div>
                        <p className="text-muted-foreground">Player Instructions</p>
                        <div className="bg-muted p-3 rounded text-xs whitespace-pre-wrap">
                          {selectedCampaign.player_detailed_instructions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Campaign ID */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Technical Details</h3>
                <div className="text-sm space-y-1">
                  <p className="text-muted-foreground">Campaign ID</p>
                  <p className="font-mono text-xs bg-muted p-2 rounded">{selectedCampaign.id}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setIsDetailsModalOpen(false)
                handleViewQR(selectedCampaign!)
              }}
            >
              <IconEye className="mr-2 h-4 w-4" />
              View QR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default function QRCodesPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
      <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
        <div className="flex items-center justify-center h-64">
          <IconLoader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    }>
      <QRCodesContent />
    </Suspense>
    </AuthGuard>
  )
}